import '@/style/global.less';
import '@/style/animation.less';
import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { SWRConfig } from 'swr';
import { Provider } from "react-redux";
import { setDialogRef } from "./utils/useDialog";
import EventMgr from "./utils/EventMgr";
import { EventName } from "./api/event/common";
import NiceModal from '@ebay/nice-modal-react'
import DialogPlus, { DialogPlusRef } from './componentsUI/Dialog/dialogPlus';
import { bindEnterKeyToButton, restrictTabToInputs } from './Core/utils';
import { sys } from './Core/utils/system';
import { AliveScope } from 'react-activation';
import {
  consumePendingApkRestoreEvent,
  restoreApkTransferFromUrl,
} from "@/features/install/apkTransfer";
import { whenEmSdkReady, trackEmEvent } from "@/features/analytics/em/sdk";
import { ScreenDesignProvider } from "@/Core/utils/hooks/ScreenDesignProvider";
import { getInitialSkinId, SkinRoot } from "@/features/skin";
import { store } from "@/features/redux/store";
import { detectClientEnvironment } from "@/features/clientEnv";
import { isOfficialApkEnvironment } from "@/features/install/withdrawalOfficialAppPrompt/policy";
import { resolvePackageGateDecision } from "@/features/install/onlinePackageRetirementGate/statusClient";
import OnlinePackageRetirementGate from "@/features/install/onlinePackageRetirementGate/OnlinePackageRetirementGate";
import { trackOnlinePackageRetirementDecision } from "@/features/install/onlinePackageRetirementGate/analytics";
import { bootstrapRestoredPidSession } from "@/features/attribution/restoredPidSession";
import { bootstrapKolVisitContext } from "@/features/attribution/kolVisit";
import { trackKolHallArrivalOnce } from "@/features/analytics/em/hallArrival";

function Index() {
  /** 弹窗中心初始化 */
  const ref = useRef<DialogPlusRef>(null);
  const skinId = useMemo(() => getInitialSkinId(), []);
  useEffect(() => {
    setDialogRef(ref);
  }, []);

  useEffect(() => {
    return whenEmSdkReady(() => { trackKolHallArrivalOnce(true); });
  }, []);

  useEffect(() => {
    sys.token = localStorage.getItem('authToken')
    /**删除html-loading */
    // const Loading = document.getElementById('Loading');
    // Loading.parentNode.removeChild(Loading)
    // setTimeout(() => {
    //   const elementLoading = document.getElementById('start-loading');
    //   document.body.removeChild(elementLoading);
    // }, 1000)

    /**前后台事件 */
    const handleVisiblity = () => {
      if (document.visibilityState == 'visible') {
        EventMgr.getInstance().dispatchEvent(EventName.EnterForeGround);
      } else {
        EventMgr.getInstance().dispatchEvent(EventName.EnterBackGround);
      }
    };
    const handleMouseEnter = () => {
      EventMgr.getInstance().dispatchEvent(EventName.EnterForeGround);
    };
    document.addEventListener('visibilitychange', handleVisiblity);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mousedown', handleMouseEnter);
    restrictTabToInputs()
    bindEnterKeyToButton()
    return () => {
      document.removeEventListener('visibilitychange', handleVisiblity);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mousedown', handleMouseEnter);
    };

  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScreenDesignProvider forceMobile={true}>
        <SkinRoot skinId={skinId}>
          <AliveScope>
            <SWRConfig
              value={{
                //默认缓存有效期
                dedupingInterval: 24 * 60 * 60 * 1000,
                //错误后自动请求间隔
                errorRetryInterval: 1000,
                //错误后自动重试次数
                errorRetryCount: 1,
                revalidateOnFocus: false,
                shouldRetryOnError: false,
              }}
            >
              <Provider store={store}>
                <NiceModal.Provider>
                  <DialogPlus ref={ref} />
                  <App />
                </NiceModal.Provider>
              </Provider>
            </SWRConfig>
          </AliveScope>
        </SkinRoot>
      </ScreenDesignProvider>
    </BrowserRouter>
  );
}

bootstrapRestoredPidSession();

restoreApkTransferFromUrl({
  location: window.location,
  history: window.history,
  localStorage: window.localStorage,
  sessionStorage: window.sessionStorage,
  __BOOTSTRAP_ATTR__: window.__BOOTSTRAP_ATTR__,
  __RUNTIME_ATTR__: window.__RUNTIME_ATTR__,
  setSysToken: (token: string) => {
    sys.token = token;
  },
});

bootstrapKolVisitContext();

whenEmSdkReady(() => {
  const pendingApkRestoreEvent = consumePendingApkRestoreEvent();
  if (pendingApkRestoreEvent) {
    trackEmEvent(
      pendingApkRestoreEvent.eventName,
      pendingApkRestoreEvent.props,
    );
  }
});

async function bootstrapReactApp() {
  if ((window as any).__BLOCK_REACT_APP__) {
    console.warn("React app blocked due to missing PID");
    return;
  }

  const rootElement = document.getElementById("root");
  if (rootElement) {
    const clientEnvironment = detectClientEnvironment();
    const gateDecision = await resolvePackageGateDecision({
      hostname: window.location.hostname,
      origin: window.location.origin,
      isOfficialOfflineApk: isOfficialApkEnvironment(clientEnvironment),
      statusUrl: window.__PACKAGE_MONITOR_STATUS_URL__,
      sessionStorage: window.sessionStorage,
    });
    trackOnlinePackageRetirementDecision(gateDecision);
    ReactDOM.createRoot(rootElement).render(
      gateDecision.mode === "force" && gateDecision.sourceKey ? (
        <OnlinePackageRetirementGate
          sourceKey={gateDecision.sourceKey}
          revision={gateDecision.revision}
          decisionSource={gateDecision.decisionSource}
          reason={gateDecision.reason}
        />
      ) : (
        <Index />
      ),
    );
  }
}

void bootstrapReactApp();
